import { create } from 'zustand';
import { DeviceLocationStatus } from '@signals/cop-ui/services/copApi';

export interface DeviceInfo {
  id: string;
  name: string;
  type: string;
  status: 'active' | 'inactive' | 'unknown';
  lastSeen?: Date;
}

interface DeviceState {
  // COP devices
  devices: DeviceLocationStatus[];
  devicesInBounds: DeviceLocationStatus[];
  selectedDevice: DeviceLocationStatus | null;
  followedDevice: DeviceLocationStatus | null;
  totalDevices: number;
  activeDevices: number;
  availableEntities: string[];
  
  // Pathfinder devices - using any[] for flexibility with existing code
  selectedDevices: Record<string, string[]>; // studentId -> deviceIds
  actualDevices: Record<string, any[]>; // eslint-disable-line @typescript-eslint/no-explicit-any
  expandedStudents: Record<string, boolean>;
  
  // Loading/error state
  isLoading: boolean;
  error: string | null;
  lastUpdated: Date | null;
  
  // COP Actions
  setDevices: (devices: DeviceLocationStatus[]) => void;
  setDevicesInBounds: (devices: DeviceLocationStatus[]) => void;
  setSelectedDevice: (device: DeviceLocationStatus | null) => void;
  setFollowedDevice: (device: DeviceLocationStatus | null) => void;
  toggleDeviceSelection: (device: DeviceLocationStatus) => void;
  setTotalDevices: (count: number) => void;
  setActiveDevices: (count: number) => void;
  
  // Pathfinder Actions
  setSelectedDevices: (devices: Record<string, string[]>) => void;
  setActualDevices: (devices: Record<string, any[]>) => void; // eslint-disable-line @typescript-eslint/no-explicit-any
  toggleStudentExpanded: (studentId: string) => void;
  setExpandedStudents: (expanded: Record<string, boolean>) => void;
  selectAllDevicesForStudent: (studentId: string) => void;
  deselectAllDevicesForStudent: (studentId: string) => void;
  toggleDeviceForStudent: (studentId: string, deviceId: string) => void;
  
  // Common Actions
  setAvailableEntities: (entities: string[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setLastUpdated: (date: Date | null) => void;
  clearDevices: () => void;
}

export const useDeviceStore = create<DeviceState>((set, get) => ({
  devices: [],
  devicesInBounds: [],
  selectedDevice: null,
  followedDevice: null,
  totalDevices: 0,
  activeDevices: 0,
  availableEntities: [],
  selectedDevices: {},
  actualDevices: {},
  expandedStudents: {},
  isLoading: false,
  error: null,
  lastUpdated: null,

  setDevices: (devices: DeviceLocationStatus[]) => set({ devices }),
  
  setDevicesInBounds: (devices: DeviceLocationStatus[]) => set({ devicesInBounds: devices }),
  
  setSelectedDevice: (device: DeviceLocationStatus | null) => set({ selectedDevice: device }),
  
  setFollowedDevice: (device: DeviceLocationStatus | null) => set({ followedDevice: device }),
  
  toggleDeviceSelection: (device: DeviceLocationStatus) => {
    const { selectedDevice, followedDevice } = get();
    if (selectedDevice?.device_id === device.device_id) {
      // Unselect
      set({ 
        selectedDevice: null, 
        followedDevice: followedDevice?.device_id === device.device_id ? null : followedDevice 
      });
    } else {
      set({ selectedDevice: device });
    }
  },
  
  setTotalDevices: (count: number) => set({ totalDevices: count }),
  
  setActiveDevices: (count: number) => set({ activeDevices: count }),
  
  setSelectedDevices: (devices: Record<string, string[]>) => set({ selectedDevices: devices }),
  
  setActualDevices: (devices: Record<string, any[]>) => set({ actualDevices: devices }), // eslint-disable-line @typescript-eslint/no-explicit-any
  
  toggleStudentExpanded: (studentId: string) =>
    set((state: DeviceState) => ({
      expandedStudents: {
        ...state.expandedStudents,
        [studentId]: !state.expandedStudents[studentId],
      },
    })),
  
  setExpandedStudents: (expanded: Record<string, boolean>) => set({ expandedStudents: expanded }),
  
  selectAllDevicesForStudent: (studentId: string) => {
    const { actualDevices, selectedDevices } = get();
    const studentDevices = actualDevices[studentId] || [];
    const deviceIds = studentDevices.map((d: any) => d.id); // eslint-disable-line @typescript-eslint/no-explicit-any
    set({
      selectedDevices: {
        ...selectedDevices,
        [studentId]: deviceIds,
      },
    });
  },
  
  deselectAllDevicesForStudent: (studentId: string) => {
    const { selectedDevices } = get();
    set({
      selectedDevices: {
        ...selectedDevices,
        [studentId]: [],
      },
    });
  },
  
  toggleDeviceForStudent: (studentId: string, deviceId: string) => {
    const { selectedDevices } = get();
    const studentDevices = selectedDevices[studentId] || [];
    const isSelected = studentDevices.includes(deviceId);
    set({
      selectedDevices: {
        ...selectedDevices,
        [studentId]: isSelected
          ? studentDevices.filter((id: string) => id !== deviceId)
          : [...studentDevices, deviceId],
      },
    });
  },
  
  setAvailableEntities: (entities: string[]) => set({ availableEntities: entities }),
  
  setLoading: (loading: boolean) => set({ isLoading: loading }),
  
  setError: (error: string | null) => set({ error }),
  
  setLastUpdated: (date: Date | null) => set({ lastUpdated: date }),
  
  clearDevices: () =>
    set({
      devices: [],
      devicesInBounds: [],
      selectedDevice: null,
      followedDevice: null,
      totalDevices: 0,
      activeDevices: 0,
      error: null,
    }),
}));
